import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Contact } from "@/components/Contact";
import { SEO } from "@/components/SEO";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { Button } from "@/components/ui/button";
import { ArrowRight, Phone, Home, Warehouse, Triangle, Shield } from "lucide-react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const materialTable = [
  { system: "Calciumsilikat", eigenschaft: "Rein mineralisch & alkalisch", vorteil: "Der Schimmel-Killer: Reguliert Feuchtigkeit aktiv." },
  { system: "Holzfaser", eigenschaft: "Ökologisch & kapillaraktiv", vorteil: "Natürliches Wohngefühl & exzellenter sommerlicher Hitzeschutz." },
  { system: "PUR/PIR Platten", eigenschaft: "WLS 022 (Hochleistung)", vorteil: "Maximale Dämmung bei extrem geringer Plattenstärke." },
  { system: "Mineralwolle", eigenschaft: "Nicht brennbar", vorteil: "Sicherer Brandschutz & effektive Schalldämmung." },
];

const specialServices = [
  {
    icon: Home,
    emoji: "🏠",
    title: "Innendämmung von Wohnräumen",
    text: "Wir verwandeln kalte Außenwände in behagliche Flächen. Durch den Einsatz kapillaraktiver Materialien (wie Calciumsilikat) verhindern wir die Bildung von Kondenswasser hinter der Dämmung – der häufigsten Ursache für verdeckten Schimmel."
  },
  {
    icon: Warehouse,
    emoji: "🧱",
    title: "Kellerdecken-Dämmung",
    text: "Schluss mit fußkalten Böden im Erdgeschoss! Eine Dämmung der Kellerdecke ist eine der wirtschaftlichsten Maßnahmen überhaupt. Wir nutzen hierfür oft kosteneffiziente EPS-Platten oder kaschierte Mineralwolle für eine saubere Optik."
  },
  {
    icon: Triangle,
    emoji: "🏡",
    title: "Dachboden- & Dachgeschoss-Dämmung",
    text: "Wärme steigt nach oben. Mit einer effizienten Dachboden-Dämmung (begehbar oder als Einblasdämmung) schließen wir das Leck in Ihrer Gebäudehülle. Für ausgebaute Dachgeschosse nutzen wir platzsparende Untersparren-Systeme."
  }
];

const faqs = [
  {
    question: "Wird der Raum durch Innendämmung viel kleiner?",
    answer: "Dank moderner Hochleistungs-Dämmstoffe wie Resol oder PIR (WLS 022) erzielen wir bereits mit wenigen Zentimetern hervorragende Ergebnisse. Der Platzverlust ist minimal."
  },
  {
    question: "Verursacht Innendämmung Schimmel?",
    answer: "Nur bei falscher Ausführung! Wir nutzen diffusionsoffene, kapillaraktive Systeme, die Feuchtigkeit puffern und wieder abgeben. Das verhindert Schimmelbildung aktiv."
  },
  {
    question: "Ist eine Förderung möglich?",
    answer: "Ja, auch Einzelmaßnahmen zur Innendämmung sind unter bestimmten Voraussetzungen förderfähig. Wir unterstützen Sie bei der technischen Dokumentation für Ihren Energieberater."
  }
];

const Innendaemmung = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Innendämmung & Schimmelschutz",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Malerfachbetrieb Stein GmbH und Co. KG",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "Mindener Str. 183",
        "addressLocality": "Herford",
        "postalCode": "32049",
        "addressRegion": "NRW",
        "addressCountry": "DE"
      },
      "telephone": "+49-5221-3832130"
    },
    "areaServed": [
      { "@type": "City", "name": "Herford" },
      { "@type": "City", "name": "Hiddenhausen" },
      { "@type": "City", "name": "Löhne" }
    ],
    "description": "Spezialist für Innendämmung in Herford. Schimmelschutz mit Calciumsilikat & Holzfaser."
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map((f) => ({
      "@type": "Question",
      "name": f.question,
      "acceptedAnswer": { "@type": "Answer", "text": f.answer },
    })),
  };

  const structuredData = { "@context": "https://schema.org", "@graph": [serviceSchema, faqSchema] };

  return (
    <div className="min-h-screen">
      <SEO 
        title="Innendämmung & Schimmelschutz: Gesundes Raumklima"
        description="Innendämmung in Herford: Calciumsilikat & Holzfaser stoppen Schimmel nachhaltig. Gesundes Raumklima, weniger Heizkosten, trockene Wände. ☎ 05221 3832130"
        keywords="Innendämmung Herford, Schimmelschutz Herford, Calciumsilikat Herford, Holzfaserdämmung, Kellerdeckendämmung Herford, Dachbodendämmung Herford"
        canonical="https://malerfachbetrieb-stein.de/innendaemmung-schimmelschutz"
        structuredData={structuredData}
      />
      <Navigation />
      <WhatsAppButton />
      
      <main>
        {/* Hero */}
        <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 bg-foreground overflow-hidden">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-20 right-10 w-72 h-72 border border-background/5 rotate-45" />
            <div className="absolute bottom-10 left-20 w-96 h-96 border border-background/5 rounded-full" />
          </div>
          <div className="container mx-auto relative z-10">
            <div className="max-w-3xl">
              <span className="inline-block text-sm font-semibold tracking-wider uppercase text-background/50 mb-4">
                Innendämmung & Schimmelschutz
              </span>
              <h1 className="font-caslon text-3xl md:text-4xl lg:text-5xl lg:text-6xl font-bold text-background mb-6 leading-tight">
                Innendämmung: Die intelligente Lösung für warmes & gesundes Wohnen
              </h1>
              <p className="text-lg md:text-xl text-background/80 mb-10 leading-relaxed max-w-2xl">
                Keine Außendämmung möglich? Wir schützen Ihre Wände von innen – hocheffizient, 
                schimmelresistent und perfekt für das Raumklima in Ihrer Herforder Immobilie.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button 
                  size="lg" 
                  className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl transition-all duration-500 hover:scale-105 group"
                  onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Jetzt kostenlose Feuchtigkeitsmessung anfragen
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                </Button>
                <a href="tel:+4952213832130">
                  <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                    <Phone className="mr-2 h-5 w-5" />
                    05221 / 3832130
                  </Button>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Problem & Lösung */}
        <section className="py-16 md:py-24 lg:py-32 bg-background">
          <div className="container mx-auto max-w-4xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground mb-8">
              Warum Innendämmung oft die einzige Wahl ist
            </h2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              In vielen Fällen ist eine Fassadendämmung von außen nicht umsetzbar – sei es durch 
              Denkmalschutz, Grenzbebauung oder bei Eigentumswohnungen. Eine fachgerechte Innendämmung 
              vom Malermeister Stein ist hier die Rettung: Sie senkt die Energiekosten sofort und hebt 
              die Oberflächentemperatur Ihrer Wände, was den Wohnkomfort massiv steigert.
            </p>
          </div>
        </section>

        {/* Material-Experten-Check */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground text-background">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-background/50 mb-4 block">
                Material-Experten-Check
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold mb-6">
                Unsere Hochleistungs-Systeme im Überblick
              </h2>
              <p className="text-lg text-background/70 leading-relaxed">
                Wir setzen nicht auf Standard-Lösungen, sondern wählen das Material passend zu Ihrer Bausubstanz:
              </p>
            </div>

            <div className="bg-background/10 rounded-3xl overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background/20">
                    <th className="text-left py-5 px-8 font-semibold text-background">System</th>
                    <th className="text-left py-5 px-8 font-semibold text-background">Material-Eigenschaft</th>
                    <th className="text-left py-5 px-8 font-semibold text-background">Ihr größter Vorteil</th>
                  </tr>
                </thead>
                <tbody>
                  {materialTable.map((row, i) => (
                    <tr key={i} className="border-b border-background/10 last:border-b-0">
                      <td className="py-5 px-8 font-medium text-background">{row.system}</td>
                      <td className="py-5 px-8 text-background/70">{row.eigenschaft}</td>
                      <td className="py-5 px-8 text-background/70">{row.vorteil}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Spezial-Leistungen */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto">
            <div className="max-w-3xl mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Spezial-Leistungen
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Dämmung im ganzen Haus
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-px bg-foreground/10 rounded-3xl overflow-hidden">
              {specialServices.map((service, index) => {
                const Icon = service.icon;
                return (
                  <div key={index} className="bg-background p-10 flex flex-col gap-6">
                    <div className="flex items-center gap-4">
                      <div className="w-14 h-14 rounded-2xl bg-foreground flex items-center justify-center">
                        <Icon className="w-7 h-7 text-background" />
                      </div>
                    </div>
                    <h3 className="font-caslon text-xl font-bold text-foreground">{service.title}</h3>
                    <p className="text-muted-foreground leading-relaxed text-sm">{service.text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* E-E-A-T Experten-Beweis */}
        <section className="py-16 md:py-24 lg:py-32 bg-background">
          <div className="container mx-auto max-w-4xl">
            <div className="bg-foreground rounded-3xl p-10 md:p-16 flex gap-6 items-start">
              <Shield className="w-10 h-10 text-background shrink-0 mt-1" />
              <div>
                <h2 className="font-caslon text-2xl md:text-3xl font-bold text-background mb-4">
                  Schimmelsanierung & Prävention in Herford
                </h2>
                <p className="text-background/80 leading-relaxed text-lg">
                  „Als zertifizierter Fachbetrieb analysieren wir vor jeder Dämmmaßnahme die Feuchtigkeit 
                  Ihrer Wände. Wir verbauen nur Systeme, die physikalisch funktionieren, damit Ihr Zuhause 
                  in Herford dauerhaft trocken und gesund bleibt."
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 md:py-24 lg:py-32 bg-muted/30">
          <div className="container mx-auto max-w-3xl">
            <div className="text-center mb-16">
              <span className="text-sm font-semibold tracking-wider uppercase text-muted-foreground mb-4 block">
                Häufige Fragen
              </span>
              <h2 className="font-caslon text-3xl md:text-4xl font-bold text-foreground">
                Häufige Fragen zur Innendämmung
              </h2>
            </div>
            
            <Accordion type="single" collapsible className="space-y-4">
              {faqs.map((faq, index) => (
                <AccordionItem 
                  key={index} 
                  value={`item-${index}`} 
                  className="bg-background border border-foreground/10 rounded-2xl px-6"
                >
                  <AccordionTrigger className="text-left hover:no-underline py-6">
                    <span className="font-semibold pr-4">{faq.question}</span>
                  </AccordionTrigger>
                  <AccordionContent className="text-muted-foreground leading-relaxed pb-6">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 md:py-24 lg:py-32 bg-foreground">
          <div className="container mx-auto text-center max-w-3xl">
            <h2 className="font-caslon text-3xl md:text-4xl font-bold text-background mb-6">
              Jetzt Energie sparen & Wohnklima verbessern
            </h2>
            <p className="text-lg text-background/80 mb-10 leading-relaxed">
              Lassen Sie uns gemeinsam prüfen, welche Dämmung für Ihr Haus in Herford die richtige ist.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-background text-foreground hover:bg-background/90 font-semibold px-10 py-7 text-base rounded-2xl"
                onClick={() => document.getElementById('kontakt')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Jetzt unverbindlichen Beratungstermin vereinbaren
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <a href="tel:+4952213832130">
                <Button size="lg" variant="outline" className="w-full sm:w-auto border-2 border-background text-background hover:bg-background/10 font-semibold px-10 py-7 text-base rounded-2xl">
                  <Phone className="mr-2 h-5 w-5" />
                  05221 / 3832130
                </Button>
              </a>
            </div>
          </div>
        </section>

        {/* Kontaktformular */}
        <Contact />
      </main>
      
      <Footer />
    </div>
  );
};

export default Innendaemmung;
